import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button, Alert, Paper } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary to catch React errors and prevent white screen crashes
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            bgcolor: 'background.default',
            p: 3,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              maxWidth: 800,
              width: '100%',
              p: 4,
              bgcolor: 'background.paper',
            }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="h5" component="div" gutterBottom>
                🚨 Application Error
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Something went wrong and the application crashed. This is likely a bug in the code.
              </Typography>
            </Alert>

            {this.state.error && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Error Details:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'grey.900',
                    color: 'error.light',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflowX: 'auto',
                  }}
                >
                  <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {this.state.error.toString()}
                  </Typography>
                </Paper>
              </Box>
            )}

            {this.state.errorInfo && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Component Stack:
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'grey.900',
                    color: 'warning.light',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    overflowX: 'auto',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {this.state.errorInfo.componentStack}
                  </Typography>
                </Paper>
              </Box>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>What to do:</strong>
              </Typography>
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                1. Click "Reload Application" below to restart
                <br />
                2. If the error persists, check browser console for details (F12)
                <br />
                3. Report this error with the details above
              </Typography>
            </Alert>

            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={this.handleReload}
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Reload Application
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}
